<!-- AddItem.vue -->
<script>
import { useMenuStore } from "@/stores/menu";
import { useTranslationStore } from "@/stores/translation";
import { storeToRefs } from "pinia";
import { useCartStore } from "@/stores/cart";
import { dishImage, photoKindForPortion } from "@/utils/dishPhoto";
import { computed, onMounted, onBeforeUnmount, watch, ref } from "vue";
import { trackProductView, contentId } from "@/analytics/track";
import { Modal } from "bootstrap";
import { useRoute } from "vue-router";

export default {
  setup() {
    const menuStore = useMenuStore();
    const cartStore = useCartStore();
    const translationStore = useTranslationStore();

    const { selectedItem } = storeToRefs(menuStore);
    const { active } = storeToRefs(cartStore);
    const modalInstance = ref(null);
    const route = useRoute();

    // Close the dish modal on ANY navigation (e.g. browser Back) so it can never linger
    // over the next page.
    watch(() => route.fullPath, () => {
      if (modalInstance.value) modalInstance.value.hide();
      if (selectedItem.value && selectedItem.value.id) menuStore.updateSelectedItem(null);
    });

    const t = (label, modules) => {
      try {
        return translationStore.translate(label, modules);
      } catch (error) {
        console.error('Error in translation:', error);
        return label || '';
      }
    };

    onMounted(() => {
      try {
        const modalElement = document.getElementById("exampleModal");
        if (modalElement) {
          // Bootstrap is a side-effect ESM import (main.js: `import 'bootstrap'`), so it is
          // NOT on window. Use the ESM Modal + getOrCreateInstance so this shares the SAME
          // instance the data-api uses — making programmatic show()/hide() actually work
          // (e.g. opening a dish's modal after navigating from a homepage card).
          modalInstance.value = Modal.getOrCreateInstance(modalElement);

          modalElement.addEventListener('hidden.bs.modal', () => {
            menuStore.updateSelectedItem(null);
            cartStore.resetActive();
          });
        } else {
          console.warn('Modal element not found');
        }
        cartStore.resetActive();
      } catch (error) {
        console.error('Error in AddItem onMounted:', error);
      }
    });

    onBeforeUnmount(() => {
      if (modalInstance.value) {
        modalInstance.value.dispose();
      }
    });

    watch(selectedItem, (newItem) => {
      if (newItem) {
        if (modalInstance.value) {
          modalInstance.value.show();
        }
        // First-party analytics + pixels: a customer is viewing this dish
        try {
          trackProductView({
            value: Number(newItem.price) || undefined,
            contentName: newItem.name,
            items: [{ item_id: contentId({ kind: 'item', meal_id: newItem.id }), item_name: newItem.name, price: Number(newItem.price) || 0, quantity: 1 }],
            meta: { id: newItem.id, name: newItem.name },
          });
        } catch (e) { /* noop */ }
        // Always clear the previous dish's selection and select THIS dish's default
        // variant. Closing the modal only clears selectedItem, not the cart's `active`
        // state — so without an unconditional reset the price + macros of the LAST
        // opened dish carried over to the next one.
        try {
          cartStore.resetActive();
          const portions = sortedPortions.value;
          if (portions && portions.length) {
            selectPortion(portions[0]);
          }
        } catch (error) {
          console.error('Error auto-selecting default portion:', error);
        }
      } else {
        if (modalInstance.value) {
          modalInstance.value.hide();
        }
      }
    });

    // Follows the chosen portion: Meal Set → the Meal Box photo, other sizes → the Main Dish photo.
    const getItemImage = (item) => dishImage(item, photoKindForPortion(currentPortion.value));

    const selectPortion = (portion) => {
      try {
        cartStore.resetActive()
        cartStore.updateActivePortion(portion);
      } catch (error) {
        console.error('Error in selectPortion:', error);
      }
    };

    const toggleAddon = (addon) => {
      try {
        const index = cartStore.active.addons.findIndex((a) => a.id === addon.id);
        if (index > -1) {
          cartStore.active.addons.splice(index, 1);
        } else {
          cartStore.active.addons.push(addon);
        }
        cartStore.updateActiveAddons();
      } catch (error) {
        console.error('Error in toggleAddon:', error);
      }
    };

    const incrementQuantity = () => {
      try {
        cartStore.updateActiveQuantity(cartStore.active.quantity + 1);
      } catch (error) {
        console.error('Error in incrementQuantity:', error);
      }
    };

    const decrementQuantity = () => {
      try {
        cartStore.updateActiveQuantity(cartStore.active.quantity - 1);
      } catch (error) {
        console.error('Error in decrementQuantity:', error);
      }
    };

    const addToCart = () => {
      try {
        if (!cartStore.active.selected_meal_portion_id) {
          alert("Please select a meal portion before adding to cart.");
          return;
        }
        cartStore.addToCart(selectedItem.value);
        cartStore.resetActive();
      } catch (error) {
        console.error('Error in addToCart:', error);
      }
    };

    const currentPortion = computed(() => {
      try {
        if (
          !selectedItem.value ||
          cartStore.active.selected_meal_portion_id == null
        ) {
          return null;
        }
        return selectedItem.value.portion.find(
          (portion) => portion.id == cartStore.active.selected_meal_portion_id
        );
      } catch (error) {
        console.error('Error in currentPortion computed:', error);
        return null;
      }
    });

    const isAddonSelected = (addon) => {
      try {
        return cartStore.active.addons.some((a) => a.id === addon.id);
      } catch (error) {
        console.error('Error in isAddonSelected:', error);
        return false;
      }
    };

    const sortedPortions = computed(() => {
      try {
        if (!selectedItem.value || !selectedItem.value.portion) {
          return [];
        }
        return [...selectedItem.value.portion].sort((a, b) => {
          const weightA = parseFloat(a.portion_weight) || 0;
          const weightB = parseFloat(b.portion_weight) || 0;
          return weightA - weightB;
        });
      } catch (error) {
        console.error('Error in sortedPortions computed:', error);
        return [];
      }
    });

    return {
      t,
      active,
      selectedItem,
      getItemImage,
      selectPortion,
      toggleAddon,
      incrementQuantity,
      decrementQuantity,
      addToCart,
      cartStore,
      currentPortion,
      isAddonSelected,
      sortedPortions,
    };
  },
};
</script>

<template>
  <!-- item detail popup -->
  <div
    class="modal modal-food-item fade"
    id="exampleModal"
    tabindex="-1"
    aria-labelledby="exampleModalLabel"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-dialog-scrollable modal-dialog-centered"
      v-if="selectedItem"
    >
      <div class="modal-content">
        <div class="modal-body">
          <div class="item-img mb-3">
            <button
              type="button"
              class="btn-close btn-close-modal"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
            <img class="mb-3" :src="getItemImage(selectedItem)" :alt="t(selectedItem.trans_code, ['menu_item']) || selectedItem.name || ''" />

            <div>
              <h5>{{ t(selectedItem.trans_code, ["menu_item"]) }}</h5>
              <p>
                {{ t(selectedItem.trans_desc_code, ["menu_item"]) }}
              </p>

              <div class="price-text">
                <span class="fw-bold" v-if="active.selected_meal_portion_id">A$ {{ active.selected_meal_addon_price + active.selected_meal_portion_price }}</span>
                <span class="fw-bold" v-else>{{ selectedItem.price }}</span>
              </div>
              <div class="d-flex justify-content-start">
                <span
                  class="badge badge-success me-2"
                  style="background-color: #576d27"
                  v-for="tag in selectedItem.tags"
                  :key="tag.id"
                  >{{ tag.name }}</span
                >
              </div>
            </div>
          </div>

          <div class="px-3 py-2">
            <h4 class="extra-status">
              Meal Size:
            </h4>
            <div class="meal-category rounded p-3">
              <div v-for="portion in sortedPortions" :key="portion.id">
                <input
                  type="radio"
                  class="btn-check"
                  name="options"
                  :id="'portion-' + portion.id"
                  :value="portion.id"
                  v-model.number="cartStore.active.selected_meal_portion_id"
                  @change="selectPortion(portion)"
                />
                <label class="btn btn-primary" :for="'portion-' + portion.id"
                  >{{ portion.portion_name }} {{
                    portion.portion_weight !== '0' ? `(${portion.portion_weight}g)`: ``
                  }}</label
                >
              </div>
            </div>
          </div>

          <!-- Addons -->
          <div
            v-if="currentPortion && currentPortion.addons.length"
            class="px-3 py-2"
          >
            <h4 class="extra-status">
              Extra Add-ons 
            </h4>
            <div class="meal-addons rounded">
              <!-- Dynamically loop through the addons -->
              <div v-for="addon in currentPortion.addons" :key="addon.id">
                <input
                  type="checkbox"
                  class="btn-check"
                  :id="'addon-' + addon.id"
                  :checked="isAddonSelected(addon)"
                  @change="toggleAddon(addon)"
                />
                <label class="btn btn-primary w-100" :for="'addon-' + addon.id">
                  <p class="mb-0">Extra {{ addon.name }}</p>
                  <p class="mb-0"><b>+</b>A$ {{ addon.price }}</p>
                </label>
              </div>
            </div>
          </div>


          <!-- nutritions facts -->
          <div class="px-3 py-2 pt-4">
            <h4 class="nutrients-facts text-center" v-if="active.isMealSet">
              Nutrition Facts – Per Meal Set (150g Dish + 150g Vegetables + 150g Rice)
            </h4>
            <h4 class="nutrients-facts text-center" v-else>
              Nutrition Facts - Per 100g Of Main Dish
            </h4>
            <div class="meal-category-table rounded">
              <table class="table w-100">
                <thead>
                  <tr>
                    <th scope="col">Protein</th>
                    <th scope="col">Carb</th>
                    <th scope="col">Fat</th>
                    <th scope="col">Calories</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="active.selected_meal_portion_id">
                    <td>{{ active.selected_meal_portion_protein == null || active.selected_meal_portion_protein == 0 ? '-' : active.selected_meal_portion_protein + ' g' }}</td>
                    <td>{{ active.selected_meal_portion_carbs == null || active.selected_meal_portion_carbs == 0 ? '-' : active.selected_meal_portion_carbs + ' g' }}</td>
                    <td>{{ active.selected_meal_portion_fat == null || active.selected_meal_portion_fat == 0 ? '-' : active.selected_meal_portion_fat + ' g' }}</td>
                    <td>{{ active.selected_meal_portion_calories == null || active.selected_meal_portion_calories == 0 ? '-' : active.selected_meal_portion_calories + ' Kcal' }}</td>
                  </tr>
                  <tr v-else>
                    <td>{{ selectedItem.protein == null || selectedItem.protein == 0 ? '-' : selectedItem.protein + ' g' }}</td>
                    <td>{{ selectedItem.carbs == null || selectedItem.carbs == 0 ? '-' : selectedItem.carbs + ' g' }}</td>
                    <td>{{ selectedItem.fats == null || selectedItem.fats == 0 ? '-' : selectedItem.fats + ' g' }}</td>
                    <td>{{ selectedItem.calories == null || selectedItem.calories == 0 ? '-' : selectedItem.calories + ' Kcal' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            class="px-3 py-2 pb-4 bg-white d-flex justify-content-between align-items-center"
          >
            <div class="item-action item-ippopup-action">
              <a
                href="javascript:void(0)"
                class="subtract"
                :class="{ 'disabled': !cartStore.active.selected_meal_portion_id }"
                @click.prevent="!cartStore.active.selected_meal_portion_id || decrementQuantity()"
              >
                <i class="fa-solid fa-minus"></i>
              </a>
              <p>{{ cartStore.active.quantity }}</p>
              <a
                href="javascript:void(0)"
                class="add"
                :class="{ 'disabled': !cartStore.active.selected_meal_portion_id }"
                @click.prevent="!cartStore.active.selected_meal_portion_id || incrementQuantity()"
              >
                <i class="fa-solid fa-plus"></i>
              </a>
            </div>
            <button
              type="button"
              class="btn btn-primary"
              @click="addToCart"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              {{ t("add_to_cart", ["ordernow"]) }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
